import { useEffect, useMemo } from 'react';
import { LAYER_CONFIGS } from '../../constants/general';

export const usePointLayerData = ({ ids, data, isLoading, error, layerManager }) => {
  const { updateLayer, setLayerLoading, setLayerError, removeLayer } = layerManager;

  // Only manage geojson point layers here. Vector-tile layers (e.g. structureFootprints) are registered separately via useLayerData and should not be cleared when the combined structure endpoint returns.
  ids = useMemo(() => ids.filter(id => LAYER_CONFIGS[id]?.mapType !== 'vectorTile'), [ids]);

  // Set loading state for all point layers
  useEffect(() => {
    ids.forEach(layerId => {
      setLayerLoading(layerId, isLoading);
    });
  }, [ids, isLoading, setLayerLoading]);

  // Set error state for all point layers
  useEffect(() => {
    if (error) {
      ids.forEach(layerId => {
        setLayerError(layerId, error);
      });
    } else {
      // Clear errors when no error
      ids.forEach(layerId => {
        setLayerError(layerId, null);
      });
    }
  }, [ids, error, setLayerError]);

  // Process and update layers when data changes
  useEffect(() => {
    if (data?.geoJsonFeatures && !isLoading && !error) {
      // Clear existing point layers first
      ids.forEach(layerId => {
        removeLayer(layerId);
      });

      // Process each geoJsonFeature and add to layer manager
      data.geoJsonFeatures.forEach(feature => {
        const layerId = feature.id;

        // Only process if this layer ID is in our expected list
        if (ids.includes(layerId)) {
          const config = LAYER_CONFIGS[layerId];

          // Add the layer data
          updateLayer(layerId, feature, {
            ...config,
            autoShow: config?.autoShow !== false, // Default to true unless explicitly false
          });
        }
      });
    } else if (!data?.geoJsonFeatures && !isLoading && !error) {
      // Remove all point layers if no data
      ids.forEach(layerId => {
        removeLayer(layerId);
      });
    }
  }, [data, isLoading, error, ids, updateLayer, removeLayer]);

  // Return useful information about the point layers
  return {
    availableLayers: data?.geoJsonFeatures?.map(f => f.id) || [],
    processedCount: data?.geoJsonFeatures?.length || 0,
  };
};
